import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { HelpCircle, Printer } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AccionesMenu } from "@/components/shell/AccionesMenu";
import { fmtFecha, fmtFechaCorta, fmtMXN, fmtPct } from "@/lib/operativo";
import { useOperativo } from "@/lib/operativo-store";

export function EstadoCuentaView() {
  const { estado, condiciones, producto, solicitud, hoy, real } = useOperativo();

  const tono =
    estado.estatus === "LIQUIDADO"
      ? "bg-emerald text-primary-foreground"
      : estado.estatus === "VENCIDO" || estado.estatus === "CARTERA VENCIDA"
        ? "bg-destructive text-primary-foreground"
        : estado.estatus === "SALDO PENDIENTE"
          ? "bg-chart-3 text-primary-foreground"
          : "bg-navy text-primary-foreground";

  const kpis = [
    { l: "Saldo de capital", v: fmtMXN(estado.saldoCapital), t: "navy" },
    { l: "Capital vencido", v: fmtMXN(estado.capitalVencido), t: "destructive" },
    { l: "Intereses vencidos (c/IVA)", v: fmtMXN(estado.adeudoInteres), t: "chart3" },
    {
      l: "Interés del período vigente (c/IVA)",
      v: fmtMXN(estado.interesVigenteTotal),
      t: "chart3",
    },
    { l: "Adeudo de moratorios (c/IVA)", v: fmtMXN(estado.adeudoMora), t: "destructive" },
    {
      l: "Mora devengada al día",
      v: fmtMXN(estado.moraAlDia + estado.ivaMoraAlDia),
      t: "destructive",
    },
    { l: "Saldo a favor (sobrepago)", v: fmtMXN(estado.sobrepago), t: "emerald" },
    { l: "Penalidad por prepago (c/IVA)", v: fmtMXN(real.penalidadPrepago), t: "destructive" },
  ];

  const Composicion = ({
    titulo,
    items,
    total,
  }: {
    titulo: string;
    items: { label: string; monto: number }[];
    total: number;
  }) => (
    <details className="mt-2 text-xs">
      <summary className="cursor-pointer text-muted-foreground">Ver composición</summary>
      <div className="mt-2 space-y-1">
        {items.map((it) => (
          <div key={it.label} className="flex justify-between gap-4">
            <span className="text-muted-foreground">{it.label}</span>
            <span className="num font-medium text-navy">{fmtMXN(it.monto)}</span>
          </div>
        ))}
        <div className="flex justify-between gap-4 border-t pt-1">
          <span className="font-medium text-navy">{titulo}</span>
          <span className="num font-semibold text-navy">{fmtMXN(total)}</span>
        </div>
      </div>
    </details>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl">
            Estado de cuenta ejecutivo
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Corte al <span className="num">{fmtFecha(hoy)}</span> · {producto.nombre} ·{" "}
            {solicitud.plazo} meses · {estado.numPagos} pagos aplicados
          </p>
        </div>
        <AccionesMenu
          acciones={[
            {
              label: "Imprimir / PDF",
              icono: <Printer className="h-4 w-4" />,
              variant: "secondary",
              onClick: () => window.print(),
            },
          ]}
        />
      </div>

      <Card className="print-block border-navy/15 p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Estatus del crédito
            </p>
            <Badge className={`mt-2 px-3 py-1 text-sm ${tono}`}>{estado.estatus}</Badge>
            <div className="mt-2 space-y-1">
              <p className="text-xs text-muted-foreground num">
                {estado.diasAtraso} días de atraso
              </p>
              <p className="text-xs font-medium text-navy num">
                Períodos vencidos sin liquidar: {estado.periodosImpagos}
              </p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1 text-xs text-muted-foreground"
                    >
                      <span className="num">
                        Umbral de cartera vencida: {estado.umbralImpagos}{" "}
                        {estado.umbralImpagos === 1 ? "impago" : "impagos"}
                      </span>
                      <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[260px] text-xs">
                    Un período cuenta como impago si ya venció y sus obligaciones exigibles (cuota,
                    moratorios, IVA, penas y rezagos) no están cubiertas. Cuando los períodos
                    vencidos sin liquidar alcanzan el umbral, el crédito se marca CARTERA VENCIDA.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="mt-1 text-xs text-muted-foreground num">
              Fecha real de último pago: {fmtFechaCorta(estado.fechaUltimoPago)}
            </p>
          </div>
          <div className="rounded-lg bg-navy-soft px-5 py-4">
            <p className="text-xs font-medium uppercase tracking-wide text-navy/70">
              Total para ponerse al corriente
            </p>
            <p className="num text-2xl font-semibold text-navy">
              {fmtMXN(estado.totalAlCorriente)}
            </p>
            <Composicion
              titulo="Total para ponerse al corriente"
              items={estado.composicionAlCorriente}
              total={estado.totalAlCorriente}
            />
          </div>
          <div className="order-first rounded-lg bg-emerald/10 px-5 py-4 sm:order-none">
            <p className="text-xs font-medium uppercase tracking-wide text-emerald">
              Total para liquidar hoy
            </p>
            <p className="num text-2xl font-semibold text-emerald">
              {fmtMXN(estado.totalLiquidarHoy)}
            </p>
            <Composicion
              titulo="Total para liquidar hoy"
              items={estado.composicionLiquidar}
              total={estado.totalLiquidarHoy}
            />
          </div>
        </div>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {kpis.map((k) => (
          <Card key={k.l} className="print-block border-navy/15 p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {k.l}
            </p>
            <p
              className={`mt-2 num text-xl font-semibold ${
                k.t === "emerald"
                  ? "text-emerald"
                  : k.t === "destructive"
                    ? "text-destructive"
                    : k.t === "chart3"
                      ? "text-chart-3"
                      : "text-navy"
              }`}
            >
              {k.v}
            </p>
          </Card>
        ))}
      </div>

      <Card className="print-block overflow-hidden border-navy/15 p-0 shadow-sm">
        <div className="border-b bg-surface px-5 py-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
            Condiciones originales y acumulados
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-4 px-5 py-5 lg:grid-cols-4">
          {[
            { l: "Monto solicitado", v: fmtMXN(solicitud.monto) },
            { l: "Monto financiado", v: fmtMXN(condiciones.montoFinanciado) },
            { l: "Neto desembolsado", v: fmtMXN(condiciones.montoNetoDesembolsado) },
            {
              l: "Desembolso neto (menos cargos iniciales)",
              v: fmtMXN(condiciones.desembolsoNeto),
            },
            { l: "Método de comisión financiada", v: condiciones.metodoComision },
            { l: `Cuota ${condiciones.etiquetaPeriodo}`, v: fmtMXN(condiciones.cuotaPeriodo) },
            {
              l: "Amortización / interés",
              v: `${solicitud.params.amortizacion.split(" ")[0]} · ${solicitud.params.calculoInteres}`,
            },
            { l: "Períodos de gracia", v: `${condiciones.periodosGracia}` },
            {
              l: "Tasa anual / mora",
              v: `${fmtPct(condiciones.tasaAnualAplicada, 0)} / ${fmtPct(condiciones.tasaMoraAnual * 100, 0)}`,
            },
            { l: "Total pagado", v: fmtMXN(real.pagado.total) },
            { l: "Capital amortizado", v: fmtMXN(real.pagado.capital) },
            {
              l: "Intereses + IVA pagados",
              v: fmtMXN(real.pagado.interes + real.pagado.ivaInteres),
            },
            { l: "Moratorios + IVA pagados", v: fmtMXN(real.pagado.mora + real.pagado.ivaMora) },
            { l: "Penas + IVA pagadas", v: fmtMXN(real.pagado.pena + real.pagado.ivaPena) },
            { l: "CAT sin IVA", v: fmtPct(condiciones.catSinIva) },
            { l: "CAT con IVA", v: fmtPct(condiciones.catConIva) },
          ].map((x) => (
            <div key={x.l}>
              <p className="text-xs text-muted-foreground">{x.l}</p>
              <p className="num text-sm font-semibold text-navy">{x.v}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
